<template>
  <div>
    <!-- Trigger -->
    <span
      v-if="$slots.default"
      class=""
      role="button"
      tabindex="0"
      @click="openModal"
      @keydown.enter.prevent="openModal"
      @keydown.space.prevent="openModal"
    >
      <slot />
    </span>
    <button
      v-else
      type="button"
      class="btn btn-outline-dark btn-sm"
      @click="openModal"
    >
      <i class="fas fa-search"></i>
    </button>

    <div
      ref="modal"
      class="modal fade"
      id="searchModal"
      tabindex="-1"
      aria-labelledby="searchModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <div class="input-group">
              <input
                ref="search_input"
                v-model="query"
                type="text"
                class="form-control"
                :placeholder="$t('Which topic are you looking for?')"
                :aria-label="$t('Search')"
                aria-describedby="button-addon2"
                @input="debouncedSearch()"
              />
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <div class="modal-body">
            <div>
              <transition-group name="component-fade">
                <!-- Message -->
                <div key="00" class="small" v-if="message">
                  {{ message }}
                </div>

                <!-- Recent searches (when input is empty) -->
                <div v-else-if="showRecent" key="01">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <span class="small text-muted">{{ $t('Recent searches') }}</span>
                    <button
                      type="button"
                      class="btn btn-link btn-sm p-0"
                      @click="clearRecentSearches"
                    >
                      {{ $t('Clear') }}
                    </button>
                  </div>
                  <div class="list-group list-group-flush">
                    <button
                      v-for="term in recentSearches"
                      :key="term"
                      type="button"
                      class="list-group-item list-group-item-action py-2"
                      @click="selectRecent(term)"
                    >
                      {{ term }}
                    </button>
                  </div>
                </div>

                <!-- Results -->
                <div v-else key="02">
                  <div v-if="results.courses?.length" class="">
                    <div v-for="course in results.courses" :key="course.id" class="">
                      <div class="card mb-2 hover-bg-gray">
                        <div class="row g-0">
                          <div class="col-3">
                            <img
                              :src="imgUrl(course.cover_photo)"
                              class="img-fluid rounded-start"
                              :alt="course.title"
                            />
                          </div>
                          <div class="col-9 d-flex align-items-center">
                            <div class="card-body">
                              <a :href="`/${course.slug}`" class="small fw-semibold text-dark stretched-link">
                                {{ course.title }}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </transition-group>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'

export default {
  data() {
    return {
      // ...existing code...
      debouncedSearch: null,
      modal: null,
      results: {},
      loading: false,
      message: null,

      // New
      query: '',
      recentSearches: [],
    }
  },
  computed: {
    // Show recent searches only when input empty, not loading, and there is history
    showRecent() {
      return !this.query && !this.loading && !this.message && this.recentSearches.length > 0
    },
  },
  methods: {
    imgUrl(url) {
      if (url) {
        return `https://djceg34j53tq.cloudfront.net/${url}`
      }
      return 'https://placehold.co/1280x800/E1D4F1/ffffff'
    },

    openModal() {
      if (!this.modal) {
        this.modal = new bootstrap.Modal(this.$refs.modal, {})
      }
      this.modal.show()
    },

    // Save up to 10 unique recent terms (most recent first, case-insensitive)
    saveRecentSearch(term) {
      const q = (term || '').trim()
      if (!q) return
      let list = this.recentSearches.filter(
        (t) => t.toLowerCase() !== q.toLowerCase()
      )
      list.unshift(q)
      if (list.length > 10) list = list.slice(0, 10)
      this.recentSearches = list
      try {
        localStorage.setItem('recent_searches', JSON.stringify(list))
      } catch (e) {
        // ignore storage errors
      }
    },

    loadRecentSearches() {
      try {
        const raw = localStorage.getItem('recent_searches')
        if (raw) {
          const parsed = JSON.parse(raw)
          if (Array.isArray(parsed)) {
            this.recentSearches = parsed.filter((t) => typeof t === 'string')
          }
        }
      } catch (e) {
        this.recentSearches = []
      }
    },

    clearRecentSearches() {
      this.recentSearches = []
      try {
        localStorage.removeItem('recent_searches')
      } catch (e) {}
    },

    selectRecent(term) {
      this.query = term
      // Cancel pending debounce and run immediately
      if (this.debouncedSearch && this.debouncedSearch.cancel) {
        this.debouncedSearch.cancel()
      }
      this.search()
      // Refocus input
      this.$nextTick(() => this.focusOnSearch())
    },

    // Changed to use this.query instead of event.target.value
    search() {
      const q = (this.query || '').trim()
      this.message = null

      if (q) {
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'search',
            search_query: q
          })
        }
        this.loading = true
        axios
          .post('/search', { q })
          .then((response) => {
            setTimeout(() => {
              this.loading = false
            }, 100)
            return response
          })
          .then(({ data }) => {
            this.results = data
          })
          .then(() => {
            // Persist the term after a successful request
            this.saveRecentSearch(q)

            if (!this.results.courses?.length) {
              this.message = `${this.$t('No results this time')}. ${this.$t('Please try again')}.`
            }
          })
          .catch((error) => {
            this.results = {}
            let status = _.get(error, 'response.status')
            if (status === 422) {
              this.message = _.get(error, 'response.data.errors.q[0]')
            }
            this.loading = false
          })
      } else {
        // Empty input: clear results to show recent searches
        this.results = {}
      }
    },

    focusOnSearch() {
      this.$refs.search_input.focus()
    },
  },
  created() {
    // ...existing code...
    this.debouncedSearch = _.debounce(this.search, 500)
    this.loadRecentSearches()
  },
  mounted() {
    this.modal = new bootstrap.Modal(this.$refs.modal, {})
    this.$refs.modal.addEventListener('shown.bs.modal', () => {
      this.focusOnSearch()
    })
  },
}
</script>

<style>
.hover-bg-gray {
  transition: background-color 0.15s ease-in-out;
}
.hover-bg-gray:hover {
  background-color: #f8f9fa;
}
</style>
